'use client';

import { useEffect, useRef, useState, type RefObject } from 'react';

/**
 * True once the element has entered the viewport — stays true afterward.
 * One-shot on purpose: re-triggering on every scroll up/down would make the
 * reveal feel like a glitch rather than an entrance. Powers the landing
 * page's scroll-reveal (see components/landing/reveal.tsx).
 */
export function useInView<T extends HTMLElement>(): {
  ref: RefObject<T | null>;
  inView: boolean;
} {
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (inView) return;
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [inView]);

  return { ref, inView };
}
